import * as React from "react";
import { Block } from "kanopi-components/Block";
import { formatCurrency } from "kanopi-components/Currency";
import { getNumberWithOrdinal } from "kw/util/utils";
import { Kontainer } from "kanopi-components/Layout/Kontainer";
import {
  LabelMedium,
  ParagraphSmall,
} from "kanopi-components/typography";
import { Placeholder } from "kanopi-components/Placeholder";
import { PolicyDocumentPollList } from "kw/util/DocumentPollList/DocumentPollList";
import { QuotePaymentFrequency } from "kw/api/zeus";
import { Sektion } from "kanopi-components/Layout/Sektion";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useContactUs } from "kw/components/ContactUs/useContactUs";

type TProps = {
  riskProfileId: string;
  policyPeriods?: TPolicyPeriod[];
  paymentFrequency?: QuotePaymentFrequency;
  periodicPrice?: string;
  startDate?: string;
  finishDate?: string;
  loading?: boolean;
  productKey?: string;
};

const StatelessPolicyConfirmedInformation: React.FC<TProps> = (props) => {
  const {
    riskProfileId,
    policyPeriods = [],
    paymentFrequency,
    periodicPrice,
    startDate,
    finishDate,
    loading,
    productKey,
  } = props;
  // Ensure we get the current period.
  const currentPeriod = policyPeriods ? policyPeriods[0]?.policy.currentPeriod : null;
  const latestTransaction = currentPeriod?.latestTransaction;
  const customerRef = latestTransaction?.paymentProfile?.customerReference;
  const [, theme] = useStyletron();
  const isMonthlyPaymentFrequency = paymentFrequency === QuotePaymentFrequency.MONTHLY;
  const { contactUs } = useContactUs();

  return (
    <Placeholder loading={loading}>
      <Block data-testid={productKey + "-accordion"}>
        {policyPeriods?.map((period) => (
          <React.Fragment key={period?.policy?.policyNumber}>
            <Kontainer>
              <Block
                backgroundColor={theme.colors.backgroundTertiary}
                borderRadius={theme.borders.radius300}
                maxWidth="480px"
                margin="auto"
                padding={theme.sizing.scale600}
                $style={{ boxSizing: "border-box" }}
              >
                <Block display="flex" flexDirection="column" alignItems="center">
                  <LabelMedium>
                    <strong>Your policy number is:</strong>
                  </LabelMedium>
                  <LabelMedium>
                    <strong>{period?.policy?.policyNumber}</strong>
                  </LabelMedium>
                </Block>
              </Block>
            </Kontainer>
            <Kontainer>
              <Sektion compact>
                {policyPeriods?.length > 0 && (
                  <>
                    <Block display="flex" justifyContent="center">
                      {latestTransaction?.amountWithSurcharge && (
                        <ParagraphSmall center>
                          Your{isMonthlyPaymentFrequency ? " first monthly" : ""}{" "}
                          payment of $
                          {formatCurrency(latestTransaction?.amountWithSurcharge)} has
                          been processed successfully.
                        </ParagraphSmall>
                      )}
                    </Block>
                    {isMonthlyPaymentFrequency && (
                      <Block maxWidth="28rem" margin="auto">
                        <ParagraphSmall center>
                          An amount of ${formatCurrency(periodicPrice)} will be charged
                          to your credit card
                          {customerRef ? ` ending with ${customerRef}` : ""} on the{" "}
                          {getNumberWithOrdinal(new Date(startDate).getDate())} of each
                          month until {new Date(finishDate).toLocaleDateString()}.
                        </ParagraphSmall>
                      </Block>
                    )}
                  </>
                )}
                <Block maxWidth="28rem" margin="auto">
                  <ParagraphSmall center>
                    An email with the policy details and documents has been sent to you.
                  </ParagraphSmall>
                </Block>
                <Block display="flex" flexDirection="column" alignItems="center">
                  <PolicyDocumentPollList riskProfileId={riskProfileId} />
                </Block>
              </Sektion>
            </Kontainer>
          </React.Fragment>
        ))}
      </Block>
    </Placeholder>
  );
};

export { StatelessPolicyConfirmedInformation };
